import { Link } from "react-router-dom";
import { t, useLang } from "../lib/i18n";

export default function FaqPage() {
  useLang();
  // The i18n-driven "core five" — kept translated so Spanish users see them too.
  const FAQ = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
    { q: t("faq.q5"), a: t("faq.a5") },
  ];

  // Extra English-only long-form Q&A. These give the page substance for
  // search engines & AdSense and answer the specific questions we get in
  // support most often. Feel free to add more over time.
  const EXTRA = [
    {
      q: "Is Reads really free?",
      a: "Yes. You can create an account, log up to ten new books a month, receive up to fifty new recommendations a month, keep a wishlist and lists, and read everything indefinitely — without paying a cent. The Reads+ subscription (€3.99 / month or €35.99 / year) unlocks unlimited logging + recommendations and removes the small ad that appears once free users hit the monthly cap. It exists so the project can pay its own hosting and API costs, not because we&apos;re trying to lock features behind a paywall.",
    },
    {
      q: "Where do the recommendations come from?",
      a: "They&apos;re generated by a language model that has access to your list of highly-rated books, but nothing else — no browsing history, no demographic info, no advertising graph. If you rated more books highly, the recommendations get sharper. If the picks feel generic, adding five to ten of your all-time favourites (especially outside mainstream bestsellers) will visibly change what shows up.",
    },
    {
      q: "How do I import my Goodreads library?",
      a: "There&apos;s no direct import right now (Goodreads doesn&apos;t offer a stable public API). The practical workflow is to download your Goodreads export CSV, sort by rating, and add your top thirty or forty books manually via Discover. It sounds tedious, but most people finish in two sittings and never think about it again.",
    },
    {
      q: "Can I use Reads on my phone?",
      a: "Yes. Reads is a Progressive Web App — from your phone browser you can &ldquo;Add to home screen&rdquo; and it&apos;ll behave like a native app, including offline access to your library. There&apos;s a native mobile app in development for iOS and Android that will use RevenueCat for subscriptions across both platforms.",
    },
    {
      q: "Do you share my data with anyone?",
      a: (
        <>We do not sell, rent, or trade your data with anyone. A small number of processors are needed to make the site work (Stripe for payments, Resend for emails, Cloudflare for the anti-bot check on signup, Anthropic for recommendations, RevenueCat for subscription state). Each one only receives the minimum data required for its specific job — never your books, ratings, or reading history. The full breakdown is in the <Link to="/legal/privacidad" className="underline">Privacy Policy</Link>.</>
      ),
    },
    {
      q: "How do I delete my account?",
      a: (
        <>Go to your Account page and hit &ldquo;Delete account&rdquo;, or email us. All your personal data (name, email, ratings, reviews, wishlist) is deleted within 30 days. Anonymised aggregate stats (things like &ldquo;how many books were logged this month&rdquo;) may be retained, but nothing linked to you.</>
      ),
    },
    {
      q: "Why do I need to verify my age when I sign up?",
      a: "Spain and the EU require a parent or guardian&apos;s permission for anyone under 14 to have an account. When you register we check the year of your date of birth — if you&apos;re under 14, we send a permission-request email to a parent or guardian, and your account stays locked until they approve it. We don&apos;t publish or share your age; it&apos;s used only for that check.",
    },
    {
      q: "Can I get a refund on Reads+?",
      a: (
        <>Under EU consumer law, the 14-day right of withdrawal doesn&apos;t apply once you&apos;ve started actively using Reads+ features. If you paid but haven&apos;t used any Reads+-only feature yet, contact us within 14 days and we&apos;ll refund in full. For everything else, you can cancel any time and keep access until the end of the period you already paid for. See the full policy in <Link to="/legal/condiciones" className="underline">Terms</Link>.</>
      ),
    },
    {
      q: "Do you use my books to train AI models?",
      a: "No. Your library, ratings, and reviews are never used to train any AI model — ours or a third party&apos;s. When the recommendation engine runs, it reads your ratings in the moment to generate suggestions, but nothing is retained by the model provider for training.",
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 fade-up" data-testid="faq-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">{t("footer.help")}</div>
      <h1 className="font-heading text-5xl text-[#0A0A0A] mt-3 mb-6 tracking-tight">{t("faq.title")}</h1>
      <p className="text-[#4A4030] mb-12 leading-relaxed text-lg">
        Common questions from readers, in order of how often they land in our inbox. If your question isn&apos;t here, the <Link to="/help" className="underline">Help page</Link> has more detail or you can just <Link to="/contact" className="underline">write to us</Link>.
      </p>
      <div className="space-y-10">
        {[...FAQ, ...EXTRA].map((q, i) => (
          <div key={i} className="border-b border-[rgba(10,10,10,0.10)] pb-8" data-testid={`faq-${i}`}>
            <h2 className="font-heading text-xl md:text-2xl text-[#0A0A0A] tracking-tight">{q.q}</h2>
            <div className="text-[#0A0A0A] mt-3 leading-relaxed">{q.a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
